/**
 * Action economy classification for D&D 5e class features.
 *
 * Detects whether a feature uses a bonus action, reaction, or is a passive/other ability
 * by checking known feature IDs and scanning description text for keywords.
 */

import type { ClassFeature } from '@character-creator/types';

export type ActionType = 'action' | 'bonus-action' | 'reaction' | 'other';

export interface CategorizedAction {
  feature: ClassFeature;
  actionType: ActionType;
}

// Known features with specific action types
const KNOWN_BONUS_ACTIONS: Set<string> = new Set([
  'second-wind',
  'action-surge',
  'rage',
  'reckless-attack',
  'cunning-action',
  'bardic-inspiration',
  'healing-word',
  'misty-step',
  'patient-defense',
  'step-of-the-wind',
  'flurry-of-blows',
  'wild-shape',
  'metamagic',
  'bonus-action-attack',
  'shield-master',
  'polearm-master',
  'great-weapon-master',
]);

const KNOWN_REACTIONS: Set<string> = new Set([
  'uncanny-dodge',
  'evasion',
  'deflect-missiles',
  'slow-fall',
  'sentinel',
  'mage-slayer',
  'war-caster',
  'riposte',
  'parry',
  'shield',
  'counterspell',
  'feather-fall',
  'absorb-elements',
  'hellish-rebuke',
]);

// Keywords that indicate bonus action
const BONUS_ACTION_KEYWORDS = [
  'bonus action',
  'as a bonus action',
  'using a bonus action',
  'you can use a bonus action',
  'use your bonus action',
];

// Keywords that indicate reaction
const REACTION_KEYWORDS = [
  'reaction',
  'as a reaction',
  'using your reaction',
  'you can use your reaction',
  'use your reaction',
  'when you are hit',
  'when a creature hits you',
  'when you take damage',
];

/**
 * Detect the action type of a class feature by checking:
 * 1. Known feature IDs
 * 2. Keywords in the description
 */
export function detectActionType(feature: ClassFeature): ActionType {
  const featureIdLower = feature.id.toLowerCase();
  const descriptionLower = feature.description.toLowerCase();
  const nameLower = feature.name.toLowerCase();

  // Check known bonus actions
  if (KNOWN_BONUS_ACTIONS.has(featureIdLower)) {
    return 'bonus-action';
  }

  // Check known reactions
  if (KNOWN_REACTIONS.has(featureIdLower)) {
    return 'reaction';
  }

  // Check description for bonus action keywords
  for (const keyword of BONUS_ACTION_KEYWORDS) {
    if (descriptionLower.includes(keyword) || nameLower.includes(keyword)) {
      return 'bonus-action';
    }
  }

  // Check description for reaction keywords
  for (const keyword of REACTION_KEYWORDS) {
    if (descriptionLower.includes(keyword) || nameLower.includes(keyword)) {
      return 'reaction';
    }
  }

  return 'other';
}

/**
 * Categorize class features into actions, bonus actions, and reactions
 */
export function categorizeFeatures(features: ClassFeature[]): {
  bonusActions: CategorizedAction[];
  reactions: CategorizedAction[];
  other: CategorizedAction[];
} {
  const bonusActions: CategorizedAction[] = [];
  const reactions: CategorizedAction[] = [];
  const other: CategorizedAction[] = [];

  for (const feature of features) {
    const actionType = detectActionType(feature);
    const categorized = { feature, actionType };

    switch (actionType) {
      case 'bonus-action':
        bonusActions.push(categorized);
        break;
      case 'reaction':
        reactions.push(categorized);
        break;
      default:
        other.push(categorized);
        break;
    }
  }

  return { bonusActions, reactions, other };
}
